/* ==========================================================================
   ANISHOP LB — theme interaction layer (Shopify)
   GSAP + ScrollTrigger. Everything degrades: with JS off the page is static
   but fully readable, and every motion path is gated behind matchMedia so
   `prefers-reduced-motion` users get the content with no movement.
   ========================================================================== */

(function () {
  "use strict";

  var hasGSAP = typeof window.gsap !== "undefined";
  if (hasGSAP && typeof window.ScrollTrigger !== "undefined") {
    gsap.registerPlugin(ScrollTrigger);
  }

  var $  = function (sel, root) { return (root || document).querySelector(sel); };
  var $$ = function (sel, root) { return Array.prototype.slice.call((root || document).querySelectorAll(sel)); };

  /* ---------------------------------------------------------------- utils */

  var toastWrap;
  function toast(msg) {
    if (!toastWrap) {
      toastWrap = document.createElement("div");
      toastWrap.className = "toast-wrap";
      toastWrap.setAttribute("aria-live", "polite");
      document.body.appendChild(toastWrap);
    }
    var el = document.createElement("div");
    el.className = "toast";
    el.innerHTML =
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">' +
      '<path d="M20 6 9 17l-5-5"/></svg><span></span>';
    $("span", el).textContent = msg;
    toastWrap.appendChild(el);
    setTimeout(function () {
      el.style.transition = "opacity .3s, transform .3s";
      el.style.opacity = "0";
      el.style.transform = "translateY(10px)";
      setTimeout(function () { el.remove(); }, 320);
    }, 3200);
  }

  /* ------------------------------------------------------- seamless loops */
  /* Duplicate marquee content so the -50% translate loop never shows a gap. */
  function primeMarquees() {
    $$("[data-loop]").forEach(function (track) {
      if (track.dataset.looped) return;
      var clone = track.cloneNode(true);
      clone.removeAttribute("data-loop");
      Array.prototype.slice.call(track.children).forEach(function (child) {
        var c = child.cloneNode(true);
        c.setAttribute("aria-hidden", "true");
        track.appendChild(c);
      });
      track.dataset.looped = "1";
    });
  }

  /* ------------------------------------------------------- sticky header */
  function initHeader() {
    var header = $(".site-header");
    if (!header) return;
    var stuck = false;
    function onScroll() {
      var should = window.scrollY > 24;
      if (should !== stuck) {
        stuck = should;
        header.classList.toggle("is-stuck", stuck);
      }
    }
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
  }

  /* ----------------------------------------------------------- mobile nav */
  function initNav() {
    var nav = $(".nav");
    // header burger plus the bottom bar's Menu button — both drive one drawer
    var toggles = $$(".nav-toggle, [data-nav-toggle]");
    var toggle = toggles[0];
    if (!toggles.length || !nav) return;

    function setOpen(open) {
      nav.classList.toggle("is-open", open);
      toggles.forEach(function (t) {
        t.setAttribute("aria-expanded", String(open));
        if (t.classList.contains("nav-toggle")) {
          t.setAttribute("aria-label", open ? "Close menu" : "Open menu");
        }
      });
      document.body.style.overflow = open ? "hidden" : "";
    }

    toggles.forEach(function (t) {
      t.addEventListener("click", function () {
        setOpen(!nav.classList.contains("is-open"));
      });
    });

    // Escape closes; every nav link closes.
    document.addEventListener("keydown", function (e) {
      if (e.key === "Escape" && nav.classList.contains("is-open")) {
        setOpen(false);
        toggle.focus();
      }
    });

    $$(".nav__link", nav).forEach(function (a) {
      a.addEventListener("click", function () { setOpen(false); });
    });

    // Reset when we cross back to desktop
    window.matchMedia("(min-width: 901px)").addEventListener("change", function (e) {
      if (e.matches) setOpen(false);
    });
  }

  /* -------------------------------------------------------- custom cursor */
  function initCursor() {
    if (!hasGSAP) return;
    if (!window.matchMedia("(hover: hover) and (pointer: fine)").matches) return;

    var dot = document.createElement("div");
    var ring = document.createElement("div");
    dot.className = "cursor";
    ring.className = "cursor-ring";
    document.body.append(dot, ring);

    var xDot  = gsap.quickTo(dot,  "x", { duration: 0.10, ease: "power3.out" });
    var yDot  = gsap.quickTo(dot,  "y", { duration: 0.10, ease: "power3.out" });
    var xRing = gsap.quickTo(ring, "x", { duration: 0.42, ease: "power3.out" });
    var yRing = gsap.quickTo(ring, "y", { duration: 0.42, ease: "power3.out" });

    var shown = false;
    window.addEventListener("mousemove", function (e) {
      if (!shown) {
        shown = true;
        gsap.to([dot, ring], { opacity: 1, duration: 0.3 });
      }
      xDot(e.clientX);  yDot(e.clientY);
      xRing(e.clientX); yRing(e.clientY);
    }, { passive: true });

    document.addEventListener("mouseleave", function () {
      shown = false;
      gsap.to([dot, ring], { opacity: 0, duration: 0.2 });
    });

    // Grow the ring over anything interactive
    var hot = "a, button, input, select, textarea, .series-card, .cat-card, .product-card, [data-cursor]";
    document.addEventListener("mouseover", function (e) {
      if (e.target.closest(hot)) ring.classList.add("is-hot");
    });
    document.addEventListener("mouseout", function (e) {
      if (e.target.closest(hot)) ring.classList.remove("is-hot");
    });
  }

  /* -------------------------------------------- split a heading into chars */
  /* Hand-rolled because GSAP SplitText is a paid plugin. Keeps the original
     text available to screen readers via aria-label on the parent. */
  function splitChars(el) {
    if (!el || el.dataset.split) return [];
    el.dataset.split = "1";
    var chars = [];

    function walk(node) {
      Array.prototype.slice.call(node.childNodes).forEach(function (child) {
        if (child.nodeType === 3) {
          var frag = document.createDocumentFragment();
          /* Group characters into words. Each .char is an inline-block, so
             without a nowrap wrapper the browser happily breaks a line between
             any two letters — "Your d / ream collec / tion". */
          child.textContent.split(/(\s+)/).forEach(function (token) {
            if (!token) return;
            if (/^\s+$/.test(token)) {
              frag.appendChild(document.createTextNode(" "));
              return;
            }
            var word = document.createElement("span");
            word.className = "word";
            token.split("").forEach(function (ch) {
              var span = document.createElement("span");
              span.className = "char";
              span.textContent = ch;
              word.appendChild(span);
              chars.push(span);
            });
            frag.appendChild(word);
          });
          node.replaceChild(frag, child);
        } else if (child.nodeType === 1) {
          walk(child);
        }
      });
    }

    el.setAttribute("aria-label", el.textContent.trim());
    walk(el);
    // Children are decorative once the label is set
    Array.prototype.slice.call(el.children).forEach(function (c) {
      c.setAttribute("aria-hidden", "true");
    });
    return chars;
  }

  /* --------------------------------------------------------- hero cinema  */
  function initHero(reduce) {
    var hero = $(".hero");
    if (!hero || !hasGSAP) return;

    var title = $(".hero__title", hero);
    var chars = splitChars(title);
    var media = $(".hero__media", hero);
    var lines = $(".speedlines", hero);

    if (reduce) {
      gsap.set(chars, { opacity: 1, y: 0 });
      gsap.set([".hero__lede", ".hero__cta", ".hero .eyebrow", ".hero__stats", ".hero__rail"], { opacity: 1, y: 0 });
      return;
    }

    /* Two things were making the intro snap into place instead of playing.
       GSAP's default lag smoothing lets a stall of up to half a second pass
       through as real elapsed time, so the timeline resumes far ahead of where
       it left off; and the timeline was starting while the browser was still
       decoding the hero photo, which is exactly when that stall happens.
       Clamp the catch-up, and wait for the photo before starting. */
    gsap.ticker.lagSmoothing(250, 33);

    var tl = gsap.timeline({ paused: true, defaults: { ease: "power3.out" } });

    var art = media && media.querySelector("img");
    var started = false;
    function start() {
      if (started) return;
      started = true;
      tl.play();
    }
    if (art && !art.complete && art.decode) {
      art.decode().then(start).catch(start);
      // never let a slow or failed image hold the intro
      setTimeout(start, 900);
    } else {
      requestAnimationFrame(start);
    }

    /* On a phone the whole intro is one short tween on one element.
       The staggered version holds the buttons 24px low and invisible until
       its turn comes up — roughly 900ms in — and if the main thread is busy
       decoding the hero image in that window, the timeline catches up in a
       single frame and everything visibly jumps into place. One element, one
       tween, started immediately: nothing to stall and nothing to snap. */
    var perChar = window.matchMedia("(min-width: 701px)").matches;

    if (!perChar) {
      gsap.set(chars, { clearProps: "all" });
      gsap.set([".hero .eyebrow", ".hero__lede", ".hero__cta .btn", ".hero__rail", ".hero__stat"],
               { clearProps: "all" });
      gsap.fromTo(".hero__copy",
        { y: 20, opacity: 0 },
        { y: 0, opacity: 1, duration: 0.5, ease: "power2.out", clearProps: "transform" });
      return;
    }

    if (perChar) {
      // 1. Title card slams in, character by character
      tl.from(chars, {
        yPercent: 118,
        rotationX: -70,
        opacity: 0,
        duration: 0.7,
        // 0.028 over ~30 characters ran the title alone to 1.7s, which left the
        // buttons appearing long after everything else and reading as a late
        // jump rather than part of the same movement.
        stagger: { each: 0.016, from: "start" },
        transformOrigin: "50% 100%"
      });
    }

    // 2. A single speed-line flash on the impact frame
    if (lines) {
      tl.fromTo(lines,
        { opacity: 0.5, scale: 1.25 },
        { opacity: 0, scale: 1, duration: 0.55, ease: "power2.out" },
        "-=0.55"
      );
    }

    // 3. Supporting copy rises
    tl.from(".hero .eyebrow", { y: 18, opacity: 0, duration: 0.5 }, "-=0.7")
      .from(".hero__lede",    { y: 22, opacity: 0, duration: 0.6 }, "-=0.35")
      .from(".hero__cta .btn",{ y: 12, opacity: 0, duration: 0.5, stagger: 0.07,
                                clearProps: "transform" }, "-=0.45")
      .from(".hero__rail",    { opacity: 0, duration: 0.7 }, "-=0.5")
      .from(".hero__stat",    { y: 20, opacity: 0, duration: 0.5, stagger: 0.07 }, "-=0.45");

    /* Watchdog. These are .from() tweens, so the headline, the lede and BOTH
       call-to-action buttons start at opacity 0 and only become visible as the
       timeline plays. If the frame loop is throttled — a backgrounded tab, iOS
       low-power mode, a cheap Android under load — the most important content
       on the page would sit invisible indefinitely. Never let that happen:
       if the intro has not finished shortly after its natural length, snap it
       to the end. */
    var settle = setTimeout(function () {
      if (tl.progress() < 1) tl.progress(1);
      // Snapping the timeline is not always enough: a tween interrupted part
      // way can leave the offset behind on the element, and a 24px offset on
      // the buttons drops them into the band below the hero. Clear it outright.
      gsap.set([".hero__cta .btn", ".hero .eyebrow", ".hero__lede"], {
        clearProps: "transform",
        opacity: 1
      });
    }, 3200);

    tl.eventCallback("onComplete", function () { clearTimeout(settle); });

    // Loading in a hidden tab pauses rAF outright; finish immediately on return.
    document.addEventListener("visibilitychange", function onShow() {
      if (document.visibilityState !== "visible") return;
      document.removeEventListener("visibilitychange", onShow);
      if (tl.progress() < 1) tl.progress(1);
      clearTimeout(settle);
    });

    // Parallax: the plate drifts slower than the page
    if (media && typeof ScrollTrigger !== "undefined") {
      gsap.to(media, {
        yPercent: 14,
        ease: "none",
        scrollTrigger: {
          trigger: hero,
          start: "top top",
          end: "bottom top",
          scrub: true
        }
      });
      // Copy floats up slightly faster, and fades as it leaves
      gsap.to(".hero__copy", {
        yPercent: -18,
        opacity: 0.15,
        ease: "none",
        scrollTrigger: { trigger: hero, start: "top top", end: "bottom top", scrub: true }
      });
    }
  }

  /* ------------------------------------------------- scroll reveal system */
  /* Triggered by native IntersectionObserver rather than ScrollTrigger: the
     content is hidden by CSS up front, so the reveal mechanism must not be
     able to silently fail. IO is cheap, reliable, and independent of scroll
     math. GSAP is used only to run the tween when it is available. */
  function initReveals(reduce) {
    var targets = $$("[data-reveal]");
    if (!targets.length) return;

    // Resolve each target to the elements that actually move
    function itemsOf(el) {
      return el.dataset.reveal === "stagger"
        ? Array.prototype.slice.call(el.children)
        : [el];
    }

    function showNow(el) {
      itemsOf(el).forEach(function (n) {
        n.style.opacity = "1";
        n.style.transform = "none";
      });
      el.style.opacity = "1";
      el.style.transform = "none";
      el.dataset.revealed = "1";
    }

    // No GSAP, reduced motion, or no IO support → just show everything
    if (!hasGSAP || reduce || !("IntersectionObserver" in window)) {
      targets.forEach(showNow);
      return;
    }

    // Set the start state synchronously so nothing flashes
    targets.forEach(function (el) {
      if (el.dataset.reveal === "stagger") {
        gsap.set(itemsOf(el), { opacity: 0, y: 26 });
        gsap.set(el, { opacity: 1, y: 0 });
      }
    });

    function play(el) {
      if (el.dataset.revealed) return;
      el.dataset.revealed = "1";
      gsap.to(itemsOf(el), {
        opacity: 1,
        y: 0,
        duration: 0.75,
        ease: "power3.out",
        stagger: el.dataset.reveal === "stagger" ? 0.07 : 0,
        clearProps: "transform"
      });
    }

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        play(entry.target);
        io.unobserve(entry.target);
      });
    }, { rootMargin: "0px 0px -12% 0px", threshold: 0.01 });

    targets.forEach(function (el) { io.observe(el); });

    /* Capability check. Content is hidden by CSS, so if intersection tracking
       is not actually running — a renderer that never paints, an embedded
       webview, some future quirk — the page would stay blank forever. Observe
       a sentinel that is unambiguously on screen: if it never reports back,
       intersection tracking is dead and everything is shown immediately. */
    var sentinel = document.createElement("div");
    sentinel.setAttribute("aria-hidden", "true");
    sentinel.style.cssText = "position:fixed;top:50%;left:0;width:2px;height:2px;pointer-events:none;opacity:0";
    document.body.appendChild(sentinel);

    var ioAlive = false;
    var probe = new IntersectionObserver(function (entries) {
      if (entries.some(function (e) { return e.isIntersecting; })) {
        ioAlive = true;
        probe.disconnect();
        sentinel.remove();
      }
    });
    probe.observe(sentinel);

    setTimeout(function () {
      if (ioAlive) return;
      probe.disconnect();
      sentinel.remove();
      io.disconnect();
      targets.forEach(showNow);
      // Native lazy-loading depends on the same machinery — force the images in too
      $$("img[loading='lazy']").forEach(function (img) { img.loading = "eager"; });
    }, 1500);
  }

  /* ----------------------------------------------------------------- cart */
  /* Real Shopify Ajax Cart. Every add-to-cart on the site is a normal product
     form, so this still works with JavaScript off - the form just posts and
     the browser follows the redirect. */

  function cartRoute(name) {
    return (window.AniShop && window.AniShop.routes && window.AniShop.routes[name]) || "/cart/" + name;
  }

  function setCartCount(n) {
    $$(".cart-count").forEach(function (b) {
      b.textContent = String(n);
      b.hidden = n === 0;
    });
    if (hasGSAP) {
      gsap.fromTo($$(".cart-count"), { scale: 1 },
        { scale: 1.5, duration: 0.14, yoyo: true, repeat: 1, ease: "power2.out" });
    }
  }

  function initCart() {
    document.addEventListener("submit", function (e) {
      var form = e.target.closest("form[action*='/cart/add']");
      if (!form) return;

      var btn = form.querySelector("[data-ajax-add]") || $("[name='add']", form);
      if (!btn) return;                       // let it post normally

      e.preventDefault();
      var original = btn.innerHTML;
      btn.disabled = true;
      btn.setAttribute("aria-busy", "true");

      fetch(cartRoute("cart_add"), {
        method: "POST",
        headers: { Accept: "application/json" },
        body: new FormData(form)
      })
        .then(function (r) { return r.json().then(function (d) { return { ok: r.ok, data: d }; }); })
        .then(function (res) {
          if (!res.ok) throw new Error(res.data.description || res.data.message || "Could not add to cart");
          return fetch(cartRoute("cart") + ".js", { headers: { Accept: "application/json" } })
            .then(function (r) { return r.json(); })
            .then(function (cart) {
              setCartCount(cart.item_count);
              var name = (res.data.product_title || res.data.title || "Item");
              toast(name + " — " + ((window.AniShop && window.AniShop.strings.addedToCart) || "added to cart"));
            });
        })
        .catch(function (err) {
          toast(err.message || "Could not add to cart");
        })
        .finally(function () {
          btn.disabled = false;
          btn.removeAttribute("aria-busy");
          btn.innerHTML = original;
        });
    });
  }

  /* ------------------------------------------------------- variant picker */
  /* The visible swatches drive a hidden <select name="id">, so the form always
     posts a real variant id. */
  function initVariants() {
    var select = $("[data-variant-select]");
    if (!select) return;

    $$("[data-swatch-group]").forEach(function (group) {
      $$(".swatch", group).forEach(function (btn) {
        btn.addEventListener("click", function () {
          $$(".swatch", group).forEach(function (b) { b.setAttribute("aria-pressed", "false"); });
          btn.setAttribute("aria-pressed", "true");
          syncVariant();
        });
      });
    });

    function syncVariant() {
      var chosen = $$("[data-swatch-group]").map(function (g) {
        var on = $(".swatch[aria-pressed='true']", g);
        return on ? on.dataset.value : null;
      });
      var match = Array.prototype.slice.call(select.options).filter(function (o) {
        return chosen.every(function (v) { return v === null || o.textContent.indexOf(v) > -1; });
      })[0];
      if (!match) return;
      select.value = match.value;
      var price = $("[data-price]");
      if (price && match.dataset.price) price.textContent = match.dataset.price;
      var add = $("[data-ajax-add]");
      if (add) add.disabled = match.disabled;
    }
  }

  /* ---------------------------------------------------------- rank tabs   */
  /* Tabbed product carousels. Panels are plain [hidden] elements, so with
     JavaScript off every tab's products are still on the page and reachable. */
  function initTabs() {
    $$(".rank__tabs").forEach(function (list) {
      var tabs = $$("[role='tab']", list);
      if (tabs.length < 2) return;

      function select(tab) {
        tabs.forEach(function (t) {
          var on = t === tab;
          t.setAttribute("aria-selected", String(on));
          t.tabIndex = on ? 0 : -1;
          var panel = document.getElementById(t.getAttribute("aria-controls"));
          if (panel) panel.hidden = !on;
        });
      }

      tabs.forEach(function (tab) {
        tab.addEventListener("click", function () { select(tab); });
      });

      // Left/right arrows move between tabs, as expected of a tablist
      list.addEventListener("keydown", function (e) {
        var i = tabs.indexOf(document.activeElement);
        if (i < 0) return;
        var next = null;
        if (e.key === "ArrowRight") next = tabs[(i + 1) % tabs.length];
        if (e.key === "ArrowLeft") next = tabs[(i - 1 + tabs.length) % tabs.length];
        if (e.key === "Home") next = tabs[0];
        if (e.key === "End") next = tabs[tabs.length - 1];
        if (!next) return;
        e.preventDefault();
        next.focus();
        select(next);
      });
    });
  }

  /* --------------------------------------------------------- facet submit */
  /* Sort re-submits the filter form so Shopify does the querying. */
  function initFacets() {
    var sel = $("[data-auto-submit]");
    if (!sel) return;
    sel.addEventListener("change", function () {
      var form = sel.form || $("#FacetForm");
      if (form) form.submit();
    });
  }

  /* ------------------------------------------------------------ accordion */
  function initAccordion() {
    $$(".acc__btn").forEach(function (btn) {
      btn.addEventListener("click", function () {
        var open = btn.getAttribute("aria-expanded") === "true";
        btn.setAttribute("aria-expanded", String(!open));
        var panel = document.getElementById(btn.getAttribute("aria-controls"));
        if (panel) panel.dataset.open = String(!open);
      });
    });
  }

  /* -------------------------------------------------------------- gallery */
  function initGallery() {
    var track = $("[data-gallery-track]");
    if (!track) return;

    var slides = $$("img", track);
    var thumbs = $$(".gallery__thumb");
    var dots = $$(".gallery__dot");
    if (slides.length < 2) return;

    function indexOfScroll() {
      var w = track.clientWidth || 1;
      return Math.max(0, Math.min(slides.length - 1, Math.round(track.scrollLeft / w)));
    }

    function mark(i) {
      thumbs.forEach(function (t, n) { t.setAttribute("aria-current", n === i ? "true" : "false"); });
      dots.forEach(function (d, n) { d.setAttribute("aria-current", n === i ? "true" : "false"); });
    }

    function goTo(i, smooth) {
      var w = track.clientWidth;
      // Safari ignores scroll-behavior in scrollTo options often enough to be
      // worth setting it on the element instead.
      track.style.scrollBehavior = smooth === false ? "auto" : "smooth";
      track.scrollLeft = i * w;
    }

    // Follow the swipe rather than fighting it: read the position after it settles
    var settle;
    track.addEventListener("scroll", function () {
      clearTimeout(settle);
      settle = setTimeout(function () { mark(indexOfScroll()); }, 70);
    }, { passive: true });

    thumbs.forEach(function (thumb, i) {
      thumb.addEventListener("click", function () { goTo(i); mark(i); });
    });

    dots.forEach(function (dot, i) {
      dot.addEventListener("click", function () { goTo(i); mark(i); });
    });

    track.addEventListener("keydown", function (e) {
      if (e.key !== "ArrowRight" && e.key !== "ArrowLeft") return;
      e.preventDefault();
      var i = indexOfScroll() + (e.key === "ArrowRight" ? 1 : -1);
      i = Math.max(0, Math.min(slides.length - 1, i));
      goTo(i);
      mark(i);
    });

    // A resize changes the snap width, so hold the current image in place
    var held = 0;
    window.addEventListener("resize", function () {
      goTo(held, false);
    });
    track.addEventListener("scroll", function () { held = indexOfScroll(); }, { passive: true });

    mark(0);
  }

  /* --------------------------------------------------------- qty stepper */
  /* ------------------------------------------------------------------------
     Cart quantities

     The Update button is gone: a change applies itself. Shopify's own
     /cart/change.js is the authority on what is allowed — it clamps a quantity
     to available stock and reports back what it actually set — so the value it
     returns is the one shown, not the one that was asked for.

     The input carries a max from the variant's stock, which stops most of it
     client-side. This is the backstop for the rest: typing a number, pasting
     one, or stock changing between page load and the click.
     ------------------------------------------------------------------------ */
  function initCartQty() {
    var form = document.querySelector(".cart-form");
    if (!form) return;

    var busy = false;

    function setBusy(on) {
      busy = on;
      form.classList.toggle("is-updating", on);
      $$(".cart-qty, [data-line-step]", form).forEach(function (el) { el.disabled = on; });
    }

    function change(line, quantity) {
      if (busy) return;
      setBusy(true);
      fetch("/cart/change.js", {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ line: line, quantity: quantity })
      })
        .then(function (r) {
          return r.json().then(function (data) { return { ok: r.ok, data: data }; });
        })
        .then(function (res) {
          /* Asking for more than exists comes back 422 with an explanation
             rather than a clamped quantity, so reloading on it would leave the
             number unchanged and say nothing. Say what Shopify said. */
          if (!res.ok) {
            setBusy(false);
            var input = form.querySelector('.cart-qty[data-line="' + line + '"]');
            if (input) input.value = input.defaultValue;
            toast(res.data.message || res.data.description || "That quantity is not available.");
            return;
          }
          window.location.reload();
        })
        .catch(function () {
          /* Network dropped mid-change: the cart is the authority on what happened,
             so re-read it rather than trust what is on screen. */
          window.location.reload();
        });
    }

    function lineOf(el) { return parseInt(el.dataset.line, 10); }

    form.addEventListener("click", function (e) {
      var step = e.target.closest("[data-line-step]");
      if (!step || step.disabled) return;
      var input = form.querySelector('.cart-qty[data-line="' + step.dataset.line + '"]');
      if (!input) return;
      var max = parseInt(input.dataset.max, 10);
      var now = parseInt(input.value, 10) || 0;
      var next = step.dataset.lineStep === "up" ? now + 1 : now - 1;
      if (next < 0) next = 0;
      if (max > 0 && next > max) next = max;
      if (next === now) return;
      change(lineOf(input), next);
    });

    form.addEventListener("change", function (e) {
      var input = e.target.closest(".cart-qty");
      if (!input) return;
      var max = parseInt(input.dataset.max, 10);
      var next = parseInt(input.value, 10);
      if (isNaN(next) || next < 0) next = 0;
      if (max > 0 && next > max) next = max;
      input.value = next;
      change(lineOf(input), next);
    });

    /* Enter in a number field would otherwise submit the form, which no longer
       has an Update button to submit to. */
    form.addEventListener("keydown", function (e) {
      if (e.key === "Enter" && e.target.closest(".cart-qty")) {
        e.preventDefault();
        e.target.blur();
      }
    });
  }

  /* The buy form's quantity box. The cart has its own stepper, which talks to
     Shopify instead of only moving a number, so this must not touch it: the
     two are told apart by qty--cart, and the buttons are checked for the
     data-step this one reads. Without both guards a cart row was picked up
     here, its step read as a missing attribute and so treated as "down", and
     the number was quietly decremented before the cart's own handler saw the
     click — which turned pressing minus on the first row into a deletion. */
  /* "View more" under a rail whose cards were all rendered and then hidden
     past the first few. Revealing is an attribute change, not a fetch: the
     products are already here, and a hidden element downloads no images, so
     the rows nobody opens cost nothing but their markup. */
  /* ------------------------------------------------ predictive search */
  /* Shopify answers /search/suggest.json as you type, so nobody has to know a
     product's whole name to find it. Prices come back from this endpoint as
     decimal strings — "58.00", not the cents the recommendations endpoint
     returns — so they are printed as given rather than divided.

     Every suggestion list on the site is wired the same way: the header panel
     and the search page's own box each declare data-search-form, so this reads
     one shape and neither carries its own copy of the logic. */
  function initSearch() {
    var forms = $$("[data-search-form]");
    if (!forms.length) return;

    var LIMIT = 6;
    var WAIT = 220;

    function money(value) {
      var n = parseFloat(value);
      return isNaN(n) ? "" : "$" + n.toFixed(2);
    }

    function clean(text) {
      return String(text).replace(/[&<>"']/g, function (c) {
        return {
          "&": "&amp;",
          "<": "&lt;",
          ">": "&gt;",
          '"': "&quot;",
          "'": "&#39;"
        }[c];
      });
    }

    /* Title a suggestion the way a product card is titled: the anime on its own
       small line, and off the front of the name so it is not said twice. */
    function split(title, known) {
      for (var i = 0; i < known.length; i++) {
        var name = known[i];
        if (!name) continue;
        if (title.toLowerCase().indexOf(name.toLowerCase()) === 0) {
          var rest = title.slice(name.length).replace(/^[\s\-–—:|]+/, "");
          if (rest.length > 3) return { series: name, name: rest };
        }
      }
      return { series: "", name: title };
    }

    function hit(p, known) {
      var parts = split(p.title, known);
      var img = p.image || (p.featured_image && p.featured_image.url);
      return (
        '<a class="search-hit" href="' + p.url + '" role="option">' +
          '<span class="search-hit__media">' +
            (img ? '<img src="' + img + '&width=120" alt="" width="60" height="60" loading="lazy">' : "") +
          "</span>" +
          '<span class="search-hit__text">' +
            (parts.series ? '<span class="search-hit__series">' + clean(parts.series) + "</span>" : "") +
            '<span class="search-hit__name">' + clean(parts.name) + "</span>" +
          "</span>" +
          '<span class="search-hit__meta">' +
            '<span class="search-hit__price">' + money(p.price) + "</span>" +
            (p.available ? "" : '<span class="search-hit__out">Sold out</span>') +
          "</span>" +
        "</a>"
      );
    }

    /* A matching series (collection) is shown above the product hits, so
       typing an anime's name links straight to its collection instead of
       only the products that happen to carry that word in their title. */
    function seriesHit(c, franchiseMap) {
      /* The homepage's "Shop by anime" picture for this collection, when it
         has one, so the suggestion matches what the customer already
         recognizes rather than a different, uncurated collection photo. */
      var img = (franchiseMap && franchiseMap[c.handle]) || (c.featured_image && c.featured_image.url);
      return (
        '<a class="search-hit search-hit--series" href="' + c.url + '" role="option">' +
          '<span class="search-hit__media">' +
            (img ? '<img src="' + img + '&width=120" alt="" width="60" height="60" loading="lazy">' : "") +
          "</span>" +
          '<span class="search-hit__text">' +
            '<span class="search-hit__series">Shop the series</span>' +
            '<span class="search-hit__name">' + clean(c.title) + "</span>" +
          "</span>" +
        "</a>"
      );
    }

    function draw(box, products, collections, terms, known, franchiseMap) {
      if (!products.length && !collections.length) {
        box.innerHTML = '<p class="search-panel__empty">Nothing matched that search.</p>';
        box.hidden = false;
        return;
      }
      box.innerHTML =
        collections.slice(0, 2).map(function (c) { return seriesHit(c, franchiseMap); }).join("") +
        products.map(function (p) { return hit(p, known); }).join("") +
        '<a class="search-panel__all" href="/search?q=' + encodeURIComponent(terms) + '">' +
          "See all results for &ldquo;" + clean(terms) + "&rdquo;" +
        "</a>";
      box.hidden = false;
    }

    forms.forEach(function (form) {
      var input = form.querySelector("[data-search-input]");
      var box = form.querySelector("[data-search-results]");
      if (!input || !box) return;

      var known = (input.dataset.series || "").split(",").map(function (n) {
        return n.trim();
      });
      var franchiseMap = {};
      (input.dataset.franchise || "").split(";;").forEach(function (pair) {
        var at = pair.indexOf("::");
        if (at === -1) return;
        franchiseMap[pair.slice(0, at)] = pair.slice(at + 2);
      });
      var timer = null;
      var inflight = null;

      function close() {
        box.hidden = true;
        box.innerHTML = "";
        input.setAttribute("aria-expanded", "false");
      }

      function ask(terms) {
        /* Abandon the answer to what was typed a moment ago, so a slow reply
           cannot land after a faster one and overwrite it. */
        if (inflight) inflight.abort();
        inflight = typeof AbortController === "function" ? new AbortController() : null;

        var url =
          "/search/suggest.json?q=" + encodeURIComponent(terms) +
          "&resources[type]=product,collection" +
          "&resources[limit]=" + LIMIT +
          "&resources[limit_scope]=each" +
          "&resources[options][unavailable_products]=last" +
          "&resources[options][fields]=title,product_type,variants.title,tag";

        fetch(url, inflight ? { signal: inflight.signal } : undefined)
          .then(function (r) { return r.json(); })
          .then(function (data) {
            var results = (data.resources || {}).results || {};
            var products = results.products || [];
            var collections = results.collections || [];
            draw(box, products, collections, terms, known, franchiseMap);
            input.setAttribute("aria-expanded", "true");
          })
          .catch(function () {
            /* Aborted, or the network dropped. Leave whatever is on screen
               rather than blanking a list that is still useful. */
          });
      }

      input.addEventListener("input", function () {
        var terms = input.value.trim();
        clearTimeout(timer);
        if (terms.length < 2) { close(); return; }
        timer = setTimeout(function () { ask(terms); }, WAIT);
      });

      input.addEventListener("keydown", function (e) {
        if (e.key === "Escape") { close(); return; }
        if (e.key !== "ArrowDown" && e.key !== "ArrowUp") return;
        var hits = $$("a", box);
        if (!hits.length) return;
        e.preventDefault();
        hits[0].focus();
      });

      box.addEventListener("keydown", function (e) {
        var hits = $$("a", box);
        var at = hits.indexOf(document.activeElement);
        if (e.key === "Escape") { close(); input.focus(); return; }
        if (e.key !== "ArrowDown" && e.key !== "ArrowUp") return;
        e.preventDefault();
        var next = e.key === "ArrowDown" ? at + 1 : at - 1;
        if (next < 0) { input.focus(); return; }
        if (next < hits.length) hits[next].focus();
      });

      document.addEventListener("click", function (e) {
        if (!form.contains(e.target)) close();
      });
    });

    /* --- the header panel, which the search icon opens -------------------- */
    var panel = document.getElementById("SearchPanel");
    if (!panel) return;
    var panelInput = panel.querySelector("[data-search-input]");

    function setOpen(open) {
      /* Start the sheet below whatever the header is actually occupying right
         now. The --header-h token is a fixed 68px and knows nothing about the
         announcement bar sitting above it, so the sheet was opening underneath
         the header and losing its top edge. Measuring is exact, and measuring
         once is enough: the page behind is locked while the panel is open, so
         the header cannot move after this. */
      if (open) {
        var head = document.querySelector(".site-header");
        var edge = head ? Math.max(0, head.getBoundingClientRect().bottom) : 0;
        panel.style.setProperty("--search-top", edge + "px");
      }
      panel.hidden = !open;
      panel.classList.toggle("is-open", open);
      document.body.style.overflow = open ? "hidden" : "";
      if (open && panelInput) panelInput.focus();
    }

    $$("[data-search-open]").forEach(function (opener) {
      opener.addEventListener("click", function (e) {
        e.preventDefault();
        setOpen(true);
      });
    });

    $$("[data-search-close]", panel).forEach(function (btn) {
      btn.addEventListener("click", function () { setOpen(false); });
    });

    /* Only the backdrop itself, never a click that landed on the sheet. */
    panel.addEventListener("click", function (e) {
      if (e.target === panel) setOpen(false);
    });

    document.addEventListener("keydown", function (e) {
      if (e.key === "Escape" && !panel.hidden) setOpen(false);
    });
  }

  function initRevealMore() {
    $$("[data-reveal-more]").forEach(function (btn) {
      var grid = document.getElementById(btn.dataset.revealMore);
      if (!grid) return;
      var step = parseInt(btn.dataset.step, 10) || 4;
      btn.addEventListener("click", function () {
        var rest = $$("[hidden]", grid);
        rest.slice(0, step).forEach(function (el) { el.removeAttribute("hidden"); });
        /* Nothing left behind it, so the button has nothing left to say. */
        if (rest.length <= step) btn.hidden = true;
      });
    });
  }

  function initQty() {
    var wrap = $(".qty:not(.qty--cart)");
    if (!wrap) return;
    var input = $("input", wrap);
    if (!input) return;
    $$("button[data-step]", wrap).forEach(function (btn) {
      btn.addEventListener("click", function () {
        var step = btn.dataset.step === "up" ? 1 : -1;
        var next = Math.min(99, Math.max(1, (parseInt(input.value, 10) || 1) + step));
        input.value = next;
      });
    });
  }

  /* --------------------------------------------------------- filter sheet */
  /* Below 1080px the filter list becomes a full-height sheet, so it needs the
     same discipline as any modal: lock the page behind it, close on Escape,
     and hand focus back to the trigger. */
  function initFilters() {
    var toggle = $(".filters-toggle");
    var panel = $(".filters");
    if (!toggle || !panel) return;

    var isSheet = function () { return window.matchMedia("(max-width: 1080px)").matches; };

    function setOpen(open) {
      panel.classList.toggle("is-open", open);
      toggle.setAttribute("aria-expanded", String(open));
      if (isSheet()) {
        document.body.style.overflow = open ? "hidden" : "";
        panel.setAttribute("aria-modal", String(open));
        panel.setAttribute("role", "dialog");
      }
      if (open) {
        var close = $(".filters__close", panel);
        if (close) close.focus();
      } else {
        toggle.focus();
      }
    }

    toggle.addEventListener("click", function () {
      setOpen(!panel.classList.contains("is-open"));
    });

    var closeBtn = $(".filters__close", panel);
    if (closeBtn) closeBtn.addEventListener("click", function () { setOpen(false); });

    // "Show results" just dismisses — filtering itself is Shopify's job
    var apply = $("[data-filters-apply]", panel);
    if (apply) apply.addEventListener("click", function () { setOpen(false); });

    var clear = $("[data-filters-clear]", panel);
    if (clear) clear.addEventListener("click", function () {
      // Reset to the markup defaults, not to blank — clearing everything would
      // switch "in stock only" off and fill the grid with sold-out products.
      $$("input[type='checkbox']", panel).forEach(function (b) {
        b.checked = b.hasAttribute("checked");
      });
    });

    document.addEventListener("keydown", function (e) {
      if (e.key === "Escape" && panel.classList.contains("is-open")) setOpen(false);
    });

    // Crossing back to the desktop layout must not leave the page locked
    window.matchMedia("(min-width: 1081px)").addEventListener("change", function (e) {
      if (!e.matches) return;
      panel.classList.remove("is-open");
      toggle.setAttribute("aria-expanded", "false");
      document.body.style.overflow = "";
      panel.removeAttribute("aria-modal");
      panel.removeAttribute("role");
    });
  }

  /* -------------------------------------------------------- newsletter UX */
  function initSignup() {
    $$("form[data-signup]").forEach(function (form) {
      var input = $("input[type='email']", form);
      var err = $(".field__error", form);
      var btn = $("button[type='submit']", form);

      form.addEventListener("submit", function (e) {
        e.preventDefault();
        if (!input) return;

        var value = input.value.trim();
        // Deliberately loose: reject obvious mistakes, not unusual-but-valid addresses
        if (!value || value.indexOf("@") < 1 || value.lastIndexOf(".") < value.indexOf("@")) {
          if (err) err.textContent = "Enter a valid email so we can send your code.";
          input.setAttribute("aria-invalid", "true");
          input.focus();
          return;
        }

        if (err) err.textContent = "";
        input.removeAttribute("aria-invalid");

        var original = btn ? btn.innerHTML : "";
        if (btn) { btn.disabled = true; btn.innerHTML = "Sending…"; }

        // Front-end only — wire this to Shopify/Klaviyo on the live theme
        setTimeout(function () {
          if (btn) { btn.disabled = false; btn.innerHTML = original; }
          form.reset();
          toast("You're in — check your inbox for 10% off.");
        }, 700);
      });

      // Clear the error the moment they start fixing it
      if (input) {
        input.addEventListener("input", function () {
          if (err && err.textContent) {
            err.textContent = "";
            input.removeAttribute("aria-invalid");
          }
        });
      }
    });
  }

  /* ------------------------------------------------------------ card tilt */
  function initTilt(reduce) {
    if (reduce || !hasGSAP) return;
    if (!window.matchMedia("(hover: hover) and (pointer: fine)").matches) return;

    $$("[data-tilt]").forEach(function (card) {
      var rx = gsap.quickTo(card, "rotationX", { duration: 0.5, ease: "power3.out" });
      var ry = gsap.quickTo(card, "rotationY", { duration: 0.5, ease: "power3.out" });

      card.addEventListener("mousemove", function (e) {
        var r = card.getBoundingClientRect();
        var px = (e.clientX - r.left) / r.width - 0.5;
        var py = (e.clientY - r.top) / r.height - 0.5;
        rx(-py * 9);
        ry(px * 11);
      });

      card.addEventListener("mouseleave", function () { rx(0); ry(0); });
      gsap.set(card, { transformPerspective: 900, transformStyle: "preserve-3d" });
    });
  }

  /* ------------------------------------------------------------ rail nudge */
  function initRails() {
    $$("[data-rail-prev], [data-rail-next]").forEach(function (btn) {
      btn.addEventListener("click", function () {
        var rail = document.getElementById(btn.dataset.railPrev || btn.dataset.railNext);
        if (!rail) return;
        var dir = btn.hasAttribute("data-rail-next") ? 1 : -1;
        rail.scrollBy({ left: dir * Math.min(rail.clientWidth * 0.8, 640), behavior: "smooth" });
      });
    });
  }

  /* ------------------------------------------------------------------ go  */
  /* ------------------------------------------------------- address forms */
  /* all_country_option_tags puts each country's regions on the option as
     data-provinces, so the region list can be built without pulling in
     Shopify's legacy shopify_common.js. */
  function initAddressForms() {
    document.querySelectorAll('select[name="address[country]"]').forEach(function (country) {
      var form = country.closest("form");
      var province = form && form.querySelector('select[name="address[province]"]');
      if (!province) return;
      var wrap = province.closest(".field") || province.parentElement;

      function fill() {
        var opt = country.options[country.selectedIndex];
        var list = [];
        try { list = JSON.parse(opt.getAttribute("data-provinces") || "[]"); } catch (err) {}

        if (!list.length) {
          province.innerHTML = "";
          if (wrap) wrap.hidden = true;
          return;
        }
        if (wrap) wrap.hidden = false;
        var want = province.getAttribute("data-default") || "";
        province.innerHTML = list.map(function (pair) {
          var sel = pair[0] === want ? " selected" : "";
          return '<option value="' + pair[0] + '"' + sel + ">" + pair[1] + "</option>";
        }).join("");
      }

      var want = country.getAttribute("data-default");
      if (want) country.value = want;
      country.addEventListener("change", fill);
      fill();
    });

    // Show/hide an address's edit panel
    document.addEventListener("click", function (e) {
      var btn = e.target.closest("[data-address-toggle]");
      if (!btn) return;
      var panel = document.getElementById(btn.getAttribute("data-address-toggle"));
      if (!panel) return;
      panel.dataset.open = panel.dataset.open === "true" ? "false" : "true";
    });
  }

  /* --------------------------------------------------------- sticky buy bar */
  /* Two Add to cart buttons on screen at once is just clutter, so the sticky
     one tucks away while the real one is visible. */
  function initBuyBar() {
    var bar = document.querySelector(".buy-bar");
    if (!bar) return;

    var inPage = document.querySelector(".pdp__info [data-add], .pdp [data-add], .buy-row [data-add]");
    if (!inPage || !("IntersectionObserver" in window)) return;

    function tuck(on) { bar.setAttribute("data-tucked", on ? "true" : "false"); }

    /* Chatway's bubble is fixed to the same corner this bar occupies. Publish
       the real height so the stylesheet can lift the bubble exactly clear of
       it rather than guessing at a number. */
    function measure() {
      var h = Math.round(bar.getBoundingClientRect().height);
      if (h > 0) document.documentElement.style.setProperty("--buy-bar-h", h + "px");
    }
    measure();
    if ("ResizeObserver" in window) new ResizeObserver(measure).observe(bar);
    else window.addEventListener("resize", measure);

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) { tuck(e.isIntersecting); });
    }, { rootMargin: "0px 0px -80px 0px", threshold: 0 });

    io.observe(inPage);
    tuck(false);
  }

  /* ------------------------------------------------------- cart cross-sell */
  /* Recommendations come from Shopify's own API. Adding from here tags the
     line and the cart, and the checkout button then carries the discount code
     so Shopify applies it — a theme cannot discount a line by itself. */
  function initCrossSell() {
    var root = document.querySelector("[data-cross-sell]");
    if (!root) return;

    var rail = root.querySelector("[data-cross-sell-rail]");
    var code = root.dataset.code || "";
    /* An automatic discount, or an app with a discount function, applies itself
       at checkout and needs no code carried through. The rail still has to show
       the saving, so the pricing is no longer gated on there being a code. */
    var auto = root.dataset.auto === "1";
    var percent = parseInt(root.dataset.percent, 10) || 0;
    var off = (code || auto) ? percent / 100 : 0;

    function money(cents) {
      return window.Shopify && Shopify.formatMoney
        ? Shopify.formatMoney(cents)
        : "$" + (cents / 100).toFixed(2);
    }

    var ceiling = parseInt(root.dataset.ceiling, 10);

    /* The cheapest variant someone could actually buy, which is the one the
       rail would add and therefore the price the card shows. */
    function fromPrice(p) {
      var open = (p.variants || []).filter(function (v) { return v.available; });
      if (!open.length) return Infinity;
      return open.reduce(function (low, v) { return v.price < low ? v.price : low; }, open[0].price);
    }

    function underCeiling(p) {
      if (!(ceiling > 0)) return true;
      return (p.variants || []).some(function (v) {
        return v.available && v.price <= ceiling;
      });
    }

    function ask(intent) {
      return fetch("/recommendations/products.json?product_id=" + root.dataset.seed +
                   "&limit=" + root.dataset.limit + "&intent=" + intent)
        .then(function (r) { return r.json(); })
        .then(function (data) {
          return (data.products || []).filter(function (p) { return p.available; });
        })
        .catch(function () { return []; });
    }

    /* Complementary recommendations are curated by hand in Search & Discovery,
       so a store that has not set any gets nothing back — which is why this
       rail was empty on every product. Related recommendations Shopify works
       out on its own, so they stand in when nothing has been curated. Curated
       ones still win when they exist: they are somebody's actual judgement. */
    ask("complementary")
      .then(function (items) { return items.length ? items : ask("related"); })
      .then(function (items) {
        if (!items.length) { root.hidden = true; return; }

        /* Nothing dearer than what is already in the basket. When the catalogue
           leaves nothing in that range — a $17 keychain next to $109 swords —
           show the recommendations anyway, cheapest first, rather than an empty
           shelf. The ceiling is a preference, not a promise printed anywhere. */
        var affordable = items.filter(underCeiling);
        if (affordable.length) {
          items = affordable;
        } else {
          items = items.slice().sort(function (a, b) { return fromPrice(a) - fromPrice(b); });
        }

        root.__items = items;
        rail.innerHTML = items.map(function (p) {
          var open = (p.variants || []).filter(function (x) { return x.available; });
          /* Prefer a variant inside the budget; if this product only got here
             because nothing was, offer its cheapest instead of nothing. */
          var avail = open.filter(function (x) { return !(ceiling > 0) || x.price <= ceiling; });
          if (!avail.length) {
            avail = open.slice().sort(function (a, b) { return a.price - b.price; });
          }
          var v = avail[0];
          if (!v) return "";
          // More than one to choose from: ask rather than pick for them
          var many = avail.length > 1 &&
            (p.options || []).length &&
            String(p.options[0].name || p.options[0]).toLowerCase() !== "title";
          var was = v.price;
          var now = Math.round(was * (1 - off));
          return '<article class="suggest-card">' +
            '<a class="suggest-card__media" href="' + p.url + '">' +
              (p.featured_image
                ? '<img src="' + p.featured_image + '&width=400" alt="" width="400" height="400" loading="lazy">'
                : "") +
            "</a>" +
            '<h3 class="suggest-card__name"><a href="' + p.url + '">' + p.title + "</a></h3>" +
            '<p class="suggest-card__price"><span class="price">' + money(now) + "</span>" +
              (off ? '<span class="price--was">' + money(was) + "</span>" : "") + "</p>" +
            (many
              ? '<button class="btn btn--sm btn--block" type="button" data-cross-pick="' + p.id + '">' +
                  (off ? "Pick &amp; save " + percent + "%" : "Choose an option") + "</button>"
              : '<button class="btn btn--sm btn--block" type="button" data-cross-add="' + v.id + '">' +
                  (off ? "Add with " + percent + "% off" : "Add to cart") + "</button>") +
            "</article>";
        }).join("");
      })
      .catch(function () { root.hidden = true; });

    var crossSheet;

    function openCrossPicker(prod, percent, off) {
      if (!crossSheet) {
        crossSheet = document.createElement("div");
        crossSheet.className = "variant-sheet";
        crossSheet.hidden = true;
        crossSheet.innerHTML =
          '<div class="variant-sheet__scrim" data-cross-close></div>' +
          '<div class="variant-sheet__panel" role="dialog" aria-modal="true">' +
            '<div class="variant-sheet__head">' +
              '<h2 class="variant-sheet__title"></h2>' +
              '<button class="variant-sheet__close" type="button" data-cross-close aria-label="Close">' +
                '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">' +
                '<path d="M6 6l12 12M18 6L6 18"/></svg></button>' +
            "</div>" +
            '<p class="variant-sheet__note"></p>' +
            '<div class="variant-sheet__body"><div class="variant-sheet__list"></div></div>' +
          "</div>";
        document.body.appendChild(crossSheet);

        crossSheet.addEventListener("click", function (e) {
          if (e.target.closest("[data-cross-close]")) { closeCrossPicker(); return; }
          var b = e.target.closest("[data-cross-variant]");
          if (!b) return;
          closeCrossPicker();
          addCrossSell(b.dataset.crossVariant);
        });

        document.addEventListener("keydown", function (e) {
          if (e.key === "Escape" && crossSheet && !crossSheet.hidden) closeCrossPicker();
        });
      }

      var optName = (prod.options && prod.options[0] &&
        (prod.options[0].name || prod.options[0])) || "an option";
      crossSheet.querySelector(".variant-sheet__title").textContent =
        "Choose " + String(optName).toLowerCase();
      crossSheet.querySelector(".variant-sheet__note").textContent =
        prod.title + (off ? " — " + percent + "% off once you pick one." : "");
      crossSheet.querySelector(".variant-sheet__list").innerHTML =
        (prod.variants || []).map(function (v) {
          return '<button class="swatch" type="button" data-cross-variant="' + v.id + '"' +
            (v.available ? "" : " disabled") + ">" + v.title +
            (v.available ? "" : ' <span class="swatch__out">sold out</span>') + "</button>";
        }).join("");

      crossSheet.hidden = false;
      document.body.style.overflow = "hidden";
      requestAnimationFrame(function () { crossSheet.dataset.open = "true"; });
      var first = crossSheet.querySelector("[data-cross-variant]:not([disabled])");
      if (first) first.focus();
    }

    function closeCrossPicker() {
      if (!crossSheet) return;
      crossSheet.dataset.open = "false";
      document.body.style.overflow = "";
      setTimeout(function () { crossSheet.hidden = true; }, 220);
    }

    function addCrossSell(variantId) {
      fetch("/cart/add.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          items: [{
            id: parseInt(variantId, 10),
            quantity: 1,
            properties: { _from_cart_offer: "1" }
          }]
        })
      })
        .then(function () {
          if (!code) return null;
          return fetch("/cart/update.js", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ attributes: { cart_offer: code } })
          });
        })
        .then(function () { window.location.reload(); });
    }

    rail.addEventListener("click", function (e) {
      var pick = e.target.closest("[data-cross-pick]");
      if (pick) {
        var prod = (root.__items || []).filter(function (x) {
          return String(x.id) === pick.dataset.crossPick;
        })[0];
        if (prod) openCrossPicker(prod, percent, off);
        return;
      }

      var btn = e.target.closest("[data-cross-add]");
      if (!btn) return;
      btn.disabled = true;

      fetch("/cart/add.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          items: [{
            id: parseInt(btn.dataset.crossAdd, 10),
            quantity: 1,
            properties: { _from_cart_offer: "1" }
          }]
        })
      })
        .then(function () {
          if (!code) return null;
          // Marks the cart so the checkout button can carry the code
          return fetch("/cart/update.js", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ attributes: { cart_offer: code } })
          });
        })
        .then(function () { window.location.reload(); })
        .catch(function () { btn.disabled = false; });
    });
  }

  /* ------------------------------------------------------- variant sheet */
  /* The sticky bar is often the only Add to cart on screen, and the options
     are far up the page. Rather than refuse the tap, bring the options to it:
     a sheet slides up, they pick, and the add goes through. */
  function initVariantSheet() {
    var bar = document.querySelector(".buy-bar");
    if (!bar) return;

    var barBtn = bar.querySelector("[data-ajax-add], [data-add], button[name='add'], .btn");
    if (!barBtn) return;

    /* The product page and the preview name their swatches differently; read
       whichever shape this page uses. */
    function groups() {
      var themed = $$("[data-swatch-group]");
      if (themed.length) {
        return themed.map(function (g) {
          var label = g.querySelector("label");
          return {
            name: label ? label.textContent.trim() : "Option",
            buttons: $$(".swatch", g)
          };
        });
      }
      var picker = document.querySelector("[data-variant-picker]");
      if (picker && !picker.hidden) {
        var nameEl = picker.querySelector("[data-variant-name]");
        return [{
          name: nameEl ? nameEl.textContent.trim() : "Option",
          buttons: $$("[data-variant]", picker)
        }];
      }
      return [];
    }

    // Shopify preselects a variant, so "pressed" is not the same as "chosen"
    function chosen(g) {
      return g.buttons.some(function (b) {
        return b.dataset.userPicked === "1";
      });
    }

    function pending() {
      return groups().filter(function (g) { return g.buttons.length > 1 && !chosen(g); });
    }

    document.addEventListener("click", function (e) {
      var b = e.target.closest(".swatch, [data-variant]");
      if (!b || b.disabled) return;
      var wrap = b.closest("[data-swatch-group], [data-variant-picker], .variant-sheet__list");
      if (wrap) {
        $$(".swatch, [data-variant]", wrap).forEach(function (x) { delete x.dataset.userPicked; });
      }
      b.dataset.userPicked = "1";
    });

    // ---------------------------------------------------------- the sheet
    var sheet;

    function build() {
      sheet = document.createElement("div");
      sheet.className = "variant-sheet";
      sheet.hidden = true;
      sheet.innerHTML =
        '<div class="variant-sheet__scrim" data-sheet-close></div>' +
        '<div class="variant-sheet__panel" role="dialog" aria-modal="true" aria-label="Choose an option">' +
          '<div class="variant-sheet__head">' +
            '<h2 class="variant-sheet__title"></h2>' +
            '<button class="variant-sheet__close" type="button" data-sheet-close aria-label="Close">' +
              '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">' +
              '<path d="M6 6l12 12M18 6L6 18"/></svg>' +
            "</button>" +
          "</div>" +
          '<div class="variant-sheet__body"></div>' +
        "</div>";
      document.body.appendChild(sheet);

      sheet.addEventListener("click", function (e) {
        if (e.target.closest("[data-sheet-close]")) { close(); return; }

        var pick = e.target.closest("[data-sheet-value]");
        if (!pick || pick.disabled) return;

        // Drive the real swatch so price and variant id follow as usual
        var real = pick.__real;
        if (real) real.click();

        if (!pending().length) {
          close();
          // The tap they made in the first place, now that it can go through
          setTimeout(function () { barBtn.click(); }, 60);
        } else {
          render();
        }
      });
    }

    function render() {
      var need = pending();
      if (!need.length) return;
      var g = need[0];

      sheet.querySelector(".variant-sheet__title").textContent = "Choose " + g.name.toLowerCase();

      var body = sheet.querySelector(".variant-sheet__body");
      body.innerHTML = '<div class="variant-sheet__list"></div>';
      var list = body.firstChild;

      g.buttons.forEach(function (src) {
        var b = document.createElement("button");
        b.type = "button";
        b.className = "swatch";
        b.setAttribute("data-sheet-value", "");

        /* Copy the name from its own element rather than flattening the whole
           button, which would fold a sold-out label into the name and read
           "Itachi confirmed Sold out" as one line. Falls back to the button's
           text for a swatch that carries no inner element. */
        var name = src.querySelector(".swatch__value");
        var value = document.createElement("span");
        value.className = "swatch__value";
        value.textContent = (name || src).textContent.trim();
        b.appendChild(value);

        if (src.classList.contains("swatch--out")) {
          b.classList.add("swatch--out");
          var out = document.createElement("span");
          out.className = "swatch__out";
          out.textContent = (src.querySelector(".swatch__out") || {}).textContent || "Sold out";
          b.appendChild(out);
        }

        if (src.disabled) b.disabled = true;
        b.__real = src;
        list.appendChild(b);
      });

      if (need.length > 1) {
        var note = document.createElement("p");
        note.className = "variant-sheet__step";
        note.textContent = "Step 1 of " + need.length;
        body.appendChild(note);
      }
    }

    var lastFocus = null;

    function open() {
      if (!sheet) build();
      render();
      lastFocus = document.activeElement;
      sheet.hidden = false;
      document.body.style.overflow = "hidden";
      requestAnimationFrame(function () { sheet.dataset.open = "true"; });
      var first = sheet.querySelector("[data-sheet-value]:not([disabled])");
      if (first) first.focus();
    }

    function close() {
      if (!sheet) return;
      sheet.dataset.open = "false";
      document.body.style.overflow = "";
      setTimeout(function () { sheet.hidden = true; }, 220);
      if (lastFocus && lastFocus.focus) lastFocus.focus();
    }

    document.addEventListener("keydown", function (e) {
      if (e.key === "Escape" && sheet && !sheet.hidden) close();
    });

    // Capture, so this runs before the add handlers and before form submit
    bar.addEventListener("click", function (e) {
      if (!e.target.closest("[data-ajax-add], [data-add], button[name='add'], .btn")) return;
      if (!pending().length) return;
      e.preventDefault();
      e.stopPropagation();
      open();
    }, true);
  }

  /* ------------------------------------------- gallery <-> variant naming */
  /* Each image that belongs to a variant carries its title, so swiping the
     gallery selects that variant and renames the piece under the heading. */
  function initGalleryVariants() {
    var track = document.querySelector("[data-gallery-track]");
    var heading = document.querySelector("[data-variant-heading]");
    if (!track || !heading) return;

    var slides = $$("img", track);
    var owners = slides.map(function (i) { return i.dataset.variantTitle || ""; });
    if (!owners.some(Boolean)) return;

    function name(text) {
      heading.textContent = text || "";
      heading.hidden = !text;
    }

    function selectByTitle(title) {
      var hit = false;
      $$("[data-swatch-group]").forEach(function (g) {
        $$(".swatch", g).forEach(function (b) {
          // A single-option product matches on the value itself
          if (b.dataset.value && title.indexOf(b.dataset.value) > -1) {
            /* A swipe is not an explicit choice, so the mark the dispatch
               below leaves behind is cleared again — unless it was already
               there, which means they tapped the swatch themselves and the
               gallery only followed. Read before the loop below: `b` is one
               of the swatches it clears, so afterwards the answer is always
               no and a real tap would be downgraded to a swipe. */
            var wasExplicit = b.dataset.userPicked === "1";
            $$(".swatch", g).forEach(function (x) {
              x.setAttribute("aria-pressed", "false");
              delete x.dataset.userPicked;
              delete x.dataset.gallerySelected;
            });
            b.setAttribute("aria-pressed", "true");
            b.dispatchEvent(new Event("click", { bubbles: true }));
            if (!wasExplicit) {
              delete b.dataset.userPicked;
              b.dataset.gallerySelected = "1";
            }
            hit = true;
          }
        });
      });
      return hit;
    }

    name(owners[0]);

    var settle;
    track.addEventListener("scroll", function () {
      clearTimeout(settle);
      settle = setTimeout(function () {
        var i = Math.round(track.scrollLeft / (track.clientWidth || 1));
        var t = owners[i] || "";
        name(t);
        if (t) selectByTitle(t);
      }, 90);
    }, { passive: true });

    // Choosing a swatch brings that variant's photo up
    document.addEventListener("click", function (e) {
      var b = e.target.closest(".swatch");
      if (!b || b.disabled || !b.dataset.value) return;
      var idx = owners.findIndex(function (t) {
        return t && t.indexOf(b.dataset.value) > -1;
      });
      if (idx < 0) return;
      name(owners[idx]);
      track.style.scrollBehavior = "smooth";
      track.scrollLeft = idx * track.clientWidth;
    });
  }

  /* ------------------------------------------------------------- lightbox */
  /* The gallery frame crops to fill the card, which looks tidy but hides the
     edges of a photo that is not square. Tapping opens the whole thing. */
  function initLightbox() {
    var track = document.querySelector("[data-gallery-track]");
    /* Review photos want full screen too, and they appear on pages with no
       product gallery at all — so the viewer is no longer tied to one. */
    var loose = $$("[data-lightbox]");
    if (!track && !loose.length) return;

    var box, boxTrack, counter;

    function sources() {
      return $$("img", track).map(function (i) {
        return { src: i.currentSrc || i.src, alt: i.alt || "" };
      });
    }

    function indexNow() {
      return Math.max(0, Math.round(track.scrollLeft / (track.clientWidth || 1)));
    }

    function build() {
      box = document.createElement("div");
      box.className = "lightbox";
      box.hidden = true;
      box.innerHTML =
        '<button class="lightbox__close" type="button" aria-label="Close">' +
          '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">' +
          '<path d="M6 6l12 12M18 6L6 18"/></svg></button>' +
        '<div class="lightbox__track" data-lightbox-track tabindex="0"></div>' +
        '<p class="lightbox__count" data-lightbox-count aria-live="polite"></p>';
      document.body.appendChild(box);

      boxTrack = box.querySelector("[data-lightbox-track]");
      counter = box.querySelector("[data-lightbox-count]");

      box.addEventListener("click", function (e) {
        // the backdrop and the close button both dismiss; the photo does not
        if (e.target.closest(".lightbox__close") || e.target === boxTrack || e.target === box) close();
      });

      var settle;
      boxTrack.addEventListener("scroll", function () {
        clearTimeout(settle);
        settle = setTimeout(count, 80);
      }, { passive: true });

      document.addEventListener("keydown", function (e) {
        if (!box || box.hidden) return;
        if (e.key === "Escape") { close(); return; }
        if (e.key === "ArrowRight" || e.key === "ArrowLeft") {
          e.preventDefault();
          var w = boxTrack.clientWidth || 1;
          var i = Math.round(boxTrack.scrollLeft / w) + (e.key === "ArrowRight" ? 1 : -1);
          var n = boxTrack.children.length;
          i = Math.max(0, Math.min(n - 1, i));
          boxTrack.style.scrollBehavior = "smooth";
          boxTrack.scrollLeft = i * w;
        }
      });
    }

    function count() {
      if (!boxTrack) return;
      var n = boxTrack.children.length;
      var i = Math.round(boxTrack.scrollLeft / (boxTrack.clientWidth || 1)) + 1;
      counter.textContent = n > 1 ? Math.min(i, n) + " / " + n : "";
    }

    var lastFocus = null;
    var fromGallery = false;

    function open(at) {
      if (!box) build();
      fromGallery = true;
      var imgs = sources();
      if (!imgs.length) return;

      boxTrack.innerHTML = imgs.map(function (s) {
        return '<img src="' + s.src + '" alt="' + s.alt.replace(/"/g, "&quot;") + '">';
      }).join("");

      lastFocus = document.activeElement;
      box.hidden = false;
      document.body.style.overflow = "hidden";
      // position before it animates in, so it opens on the photo they tapped
      boxTrack.style.scrollBehavior = "auto";
      boxTrack.scrollLeft = at * boxTrack.clientWidth;
      count();
      requestAnimationFrame(function () {
        box.dataset.open = "true";
        boxTrack.style.scrollBehavior = "smooth";
      });
      box.querySelector(".lightbox__close").focus();
    }

    function close() {
      if (!box || box.hidden) return;
      // hand the position back so the page shows what they were looking at
      var i = Math.round(boxTrack.scrollLeft / (boxTrack.clientWidth || 1));
      box.dataset.open = "false";
      document.body.style.overflow = "";
      setTimeout(function () { box.hidden = true; }, 200);
      if (track && fromGallery) {
        track.style.scrollBehavior = "auto";
        track.scrollLeft = i * track.clientWidth;
      }
      if (lastFocus && lastFocus.focus) lastFocus.focus();
    }

    /* A single photo that belongs to no gallery — a review shot, say. */
    function openOne(src, alt) {
      if (!box) build();
      fromGallery = false;
      boxTrack.innerHTML = '<img src="' + src + '" alt="' + String(alt || "").replace(/"/g, "&quot;") + '">';
      lastFocus = document.activeElement;
      box.hidden = false;
      document.body.style.overflow = "hidden";
      boxTrack.style.scrollBehavior = "auto";
      boxTrack.scrollLeft = 0;
      count();
      requestAnimationFrame(function () { box.dataset.open = "true"; });
      box.querySelector(".lightbox__close").focus();
    }

    document.addEventListener("click", function (e) {
      var hit = e.target.closest("[data-lightbox]");
      if (!hit) return;
      var img = hit.querySelector("img");
      var full = hit.getAttribute("href") || (img && (img.currentSrc || img.src));
      if (!full) return;
      e.preventDefault();
      openOne(full, img ? img.alt : "");
    });

    if (!track) return;

    track.addEventListener("click", function (e) {
      if (!e.target.closest("img")) return;
      e.preventDefault();
      open(indexNow());
    });

    // it is a button in all but markup
    track.style.cursor = "zoom-in";
    track.setAttribute("role", "button");
    track.setAttribute("aria-label", "Open photo full screen");
    track.addEventListener("keydown", function (e) {
      if (e.key === "Enter" || e.key === " ") { e.preventDefault(); open(indexNow()); }
    });
  }

  function boot() {
    document.documentElement.classList.add("js-ready");

    primeMarquees();
    initHeader();
    initNav();
    initCart();
    initAccordion();
    initGallery();
    initBuyBar();
    initLightbox();
    initVariantSheet();
    initGalleryVariants();
    initCrossSell();
    initQty();
    initCartQty();
    initRevealMore();
    initSearch();
    initVariants();
    initFilters();
    initFacets();
    initTabs();
    initSignup();
    initRails();
    initCursor();
    initAddressForms();

    if (hasGSAP) {
      var mm = gsap.matchMedia();
      mm.add({
        reduce: "(prefers-reduced-motion: reduce)",
        ok: "(prefers-reduced-motion: no-preference)"
      }, function (ctx) {
        var reduce = !!ctx.conditions.reduce;
        initHero(reduce);
        initReveals(reduce);
        initTilt(reduce);
      });
    } else {
      initReveals(true);
    }
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }
})();
